import type {
  AttributionRowData,
  DimensionFilter,
} from '@/features/attribution/types/attribution-types';
import { getCustomLabelFromRow, getFilterValueFromRow } from '@/features/attribution/helpers/attribution-row-keys';

import { ensureViewExists } from './ensure-view-exists';
import type { ImmerSet, SelectionActions, StoreGet } from '../../types/filter-actions-types';

// ============================================================================
// Action Creator
// ============================================================================

export function createSelectionActions(
  set: ImmerSet,
  get: StoreGet
): SelectionActions {
  return {
    updateSelectedRows: (viewId, dimension, newRows) => {
      if (!dimension) return;

      set(state => {
        const view = ensureViewExists(state, viewId);
        const existingDimData = view.selectedRowsByDimension?.[dimension] ?? {
          rows: {},
          data: {},
        };

        if (!view.selectedRowsByDimension) {
          view.selectedRowsByDimension = {};
        }

        view.selectedRowsByDimension[dimension] = {
          ...existingDimData,
          rows: newRows,
        };
      });
    },

    updateRowsData: (viewId, dimension, rowId, data) => {
      if (!dimension || !rowId || !data) return;

      set(state => {
        const view = ensureViewExists(state, viewId);
        const existingDimData = view.selectedRowsByDimension?.[dimension] ?? {
          rows: {},
          data: {},
        };

        if (!view.selectedRowsByDimension) {
          view.selectedRowsByDimension = {};
        }

        view.selectedRowsByDimension[dimension] = {
          ...existingDimData,
          data: {
            ...existingDimData.data,
            [rowId]: data,
          },
        };
      });
    },

    setSelectedRowsFromTable: (viewId, dimension, tableRows) => {
      if (!dimension) return;

      const newSelections: Record<string, boolean> = {};
      const newRowsData: Record<string, AttributionRowData> = {};

      if (tableRows && tableRows.length > 0) {
        tableRows.forEach(row => {
          if (!row?.id || !row?.original) {
            return;
          }

          newSelections[row.id] = true;
          newRowsData[row.id] = row.original;
        });
      }

      set(state => {
        const view = ensureViewExists(state, viewId);
        if (!view.selectedRowsByDimension) {
          view.selectedRowsByDimension = {};
        }

        view.selectedRowsByDimension[dimension] = {
          rows: newSelections,
          data: newRowsData,
        };
      });
    },

    clearAllSelections: (viewId) => {
      set(state => {
        const view = ensureViewExists(state, viewId);
        view.selectedRowsByDimension = {};
      });
    },

    clearSelectedDimensions: (viewId, dimensions) => {
      if (!dimensions || dimensions.length === 0) return;

      set(state => {
        const view = ensureViewExists(state, viewId);
        if (!view.selectedRowsByDimension) return;

        dimensions.forEach(dim => {
          delete view.selectedRowsByDimension![dim];
        });
      });
    },

    getSelectedCountForDimension: (viewId, dim) => {
      const state = get();
      const view = state.views[viewId];
      if (!view?.selectedRowsByDimension) return 0;

      const dimData = view.selectedRowsByDimension[dim];
      if (!dimData) return 0;

      return Object.values(dimData.rows).filter(Boolean).length;
    },

    buildFiltersFromSelections: (viewId, dimension) => {
      const state = get();
      const view = state.views[viewId];
      if (!view?.selectedRowsByDimension) return [];

      const dimData = view.selectedRowsByDimension[dimension];
      if (!dimData) return [];

      const { rows, data } = dimData;

      const filters: DimensionFilter[] = [];
      for (const [rowId, selected] of Object.entries(rows)) {
        if (!selected) continue;

        const rowData = data[rowId];
        if (!rowData) continue;

        const filterValue = getFilterValueFromRow(rowData, dimension);
        if (!filterValue) continue;

        filters.push({
          dimension,
          value: filterValue,
          rowId,
          customLabel: getCustomLabelFromRow(rowData, dimension),
        });
      }
      return filters;
    },

    syncSelectedRowsWithFilters: (viewId, dimensionFilters) => {
      if (!dimensionFilters || dimensionFilters.length === 0) return;

      set(state => {
        const view = ensureViewExists(state, viewId);
        if (!view.selectedRowsByDimension) {
          view.selectedRowsByDimension = {};
        }

        dimensionFilters.forEach(filterGroup => {
          if (!Array.isArray(filterGroup)) return;

          filterGroup.forEach(filter => {
            const { dimension, value } = filter;
            const dimData = view.selectedRowsByDimension![dimension];

            // If we have filters but no selected rows for this dimension,
            // create a synthetic selection to maintain consistency
            if (!dimData || Object.keys(dimData.rows).length === 0) {
              const syntheticRowId = filter.rowId ?? `${dimension}_${value}`;
              const syntheticRowData = {
                [dimension]: value,
                id: syntheticRowId,
              };

              view.selectedRowsByDimension![dimension] = {
                rows: { [syntheticRowId]: true },
                data: { [syntheticRowId]: syntheticRowData },
              };
            }
          });
        });
      });
    },

    ensureRowSelectionForFilters: (viewId, dimension, filters) => {
      if (!filters || filters.length === 0) return;

      set(state => {
        const view = ensureViewExists(state, viewId);
        if (!view.selectedRowsByDimension) {
          view.selectedRowsByDimension = {};
        }

        const dimData = view.selectedRowsByDimension[dimension];
        const relevantFilters = filters.filter(f => f.dimension === dimension);

        if (
          relevantFilters.length > 0 &&
          (!dimData || Object.keys(dimData.rows).length === 0)
        ) {
          const newRows: Record<string, boolean> = {};
          const newData: Record<string, AttributionRowData> = {};

          relevantFilters.forEach(filter => {
            const rowId = filter.rowId ?? `${dimension}_${filter.value}`;
            newRows[rowId] = true;
            newData[rowId] = {
              [dimension]: filter.value,
              id: rowId,
              ...(filter.customLabel && { customLabel: filter.customLabel }),
            };
          });

          view.selectedRowsByDimension[dimension] = {
            rows: newRows,
            data: newData,
          };
        }
      });
    },
  };
}
